import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { YourRound_Booking$key } from "./__generated__/YourRound_Booking.graphql";
import { YourRound_Session$key } from "./__generated__/YourRound_Session.graphql";
import { ConfigureActivityManagementRound } from "./components/ConfigureActivityManagementRound/ConfigureActivityManagementRound";
import { ConfigureRound } from "./components/ConfigureRound/ConfigureRound";
import { SelectActivityManagementRound } from "./components/SelectActivityManagementRound/SelectActivityManagementRound";
import { SelectRound } from "./components/SelectRound/SelectRound";

export const YourRound = ({
  bookingRef,
  sessionRef,
  membershipTypeId,
}: {
  bookingRef: YourRound_Booking$key | null;
  sessionRef: YourRound_Session$key | null;
  membershipTypeId: string;
}) => {
  const booking = useFragment(YourRoundFragmentString, bookingRef);
  const session = useFragment(YourRoundSessionFragmentString, sessionRef);
  const isGolf = booking?.startSlot?.agenda?.facility?.type === "GOLF_COURSE";
  const isPrivateInviteFlow =
    booking?.rounds?.edges?.some(
      (edge) => edge?.node?.__typename === "MemberRound"
    ) ?? false;
  const requiresPrePayment =
    booking?.startSlot?.agenda?.facility?.publicBookingPermissionSet
      ?.prePaymentRequirement === "FULL";
  const shouldUseConfigureRoundPath =
    isPrivateInviteFlow || !requiresPrePayment;

  const playerCount = booking?.rounds?.count ?? 0;

  return (
    <VStack paddingHorizontal={5} backgroundColor="surface-primary">
      {isGolf ? (
        <>
          {shouldUseConfigureRoundPath ? (
            <ConfigureRound bookingRef={booking} />
          ) : (
            <SelectRound
              bookingRef={booking}
              firstName={session?.currentClientProfile?.member?.firstName ?? ""}
              lastName={session?.currentClientProfile?.member?.lastName ?? ""}
            />
          )}
        </>
      ) : (
        <>
          {shouldUseConfigureRoundPath ? (
            <ConfigureActivityManagementRound
              shareId={booking?.shareId ?? ""}
              membershipTypeId={membershipTypeId}
              playerCount={playerCount ?? 0}
              duration={booking?.durationMins ?? 0}
            />
          ) : (
            <SelectActivityManagementRound
              bookingRef={booking}
              firstName={session?.currentClientProfile?.member?.firstName ?? ""}
              lastName={session?.currentClientProfile?.member?.lastName ?? ""}
            />
          )}
        </>
      )}
    </VStack>
  );
};

const YourRoundSessionFragmentString = graphql`
  fragment YourRound_Session on Session {
    currentClientProfile {
      member {
        ... on Member {
          firstName
          lastName
        }
        ... on Customer {
          firstName
          lastName
        }
      }
    }
  }
`;

const YourRoundFragmentString = graphql`
  fragment YourRound_Booking on Booking {
    ...SelectRound_Booking
    ...ConfigureRound_Booking
    ...SelectActivityManagementRound_Booking
    id
    shareId
    startSlot {
      agenda {
        facility {
          type
          publicBookingPermissionSet {
            prePaymentRequirement
          }
        }
      }
    }
    ... on StandardBooking {
      durationMins
    }
    rounds {
      count
      edges {
        node {
          __typename
        }
      }
    }
  }
`;
